import { Select, Spin } from 'antd';
import Table, { ColumnProps } from 'antd/lib/table';
import { observer } from 'mobx-react';
import React from 'react';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { SearchInput } from '../../components/search-input';
import { Tag } from '../../components/tag';
import { useRppPrizeCouponStore } from '../../contexts/mobx';
import type {
    RppPrizeCoupon,
    RppPrizeCouponSearchParams,
    RppPrizeCouponType,
} from '../../stores/rpp-prize-coupon';
import { convertUtcToLocalString } from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import { InventoryProgress } from './inventory-progress';
import style from './style.module.scss';

const LIST_DATE_FORMAT = 'YYYY-MM-DD';

function enTitle(coupon: RppPrizeCoupon): string {
    const en = coupon.items?.find((item) => item.language === 'en');
    return en?.title || coupon.items?.[0]?.title || '—';
}

function provincesLabel(coupon: RppPrizeCoupon): string {
    if (coupon.provinces?.length) {
        return coupon.provinces.map((province) => province.name).join(', ');
    }
    return String(coupon.provinceIds?.length ?? 0);
}

function typeLabel(type: RppPrizeCouponType): string {
    return type === 'in_store' ? 'In-store' : 'Coupon';
}

export const RppPrizeCouponsPage = observer(() => {
    const store = useRppPrizeCouponStore();
    const navigation = useNavigation();
    const search = useSearchParams<RppPrizeCouponSearchParams>();
    const [searchValue, setSearchValue] = React.useState<string>(
        String(search.params.q ?? '')
    );

    React.useEffect(() => {
        store.fetchList(search.params);
    }, [search.params, store]);

    const handleSearchClick = () =>
        search.setParams({ q: searchValue, page: 0 });
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        if (value === '') {
            search.setParams({ q: value, page: 0 });
        }
    };
    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
        e.key === 'Enter' && search.setParams({ q: searchValue, page: 0 });

    const columns = React.useMemo<ColumnProps<RppPrizeCoupon>[]>(
        () => [
            {
                title: 'Id',
                dataIndex: 'id',
                key: 'id',
                width: '5%',
                ellipsis: true,
            },
            {
                title: 'Title (EN)',
                key: 'title',
                width: '17%',
                ellipsis: true,
                render: (_: unknown, record: RppPrizeCoupon) => enTitle(record),
            },
            {
                title: 'Type',
                key: 'type',
                width: '8%',
                render: (_: unknown, record: RppPrizeCoupon) =>
                    typeLabel(record.type),
            },
            {
                title: 'Provinces',
                key: 'provinces',
                width: '12%',
                ellipsis: true,
                render: (_: unknown, record: RppPrizeCoupon) =>
                    provincesLabel(record),
            },
            {
                title: 'Inventory progress',
                key: 'inventoryProgress',
                width: '28%',
                render: (_: unknown, record: RppPrizeCoupon) => (
                    <InventoryProgress coupon={record} />
                ),
            },
            {
                title: 'Active',
                key: 'isActive',
                width: '8%',
                render: (_: unknown, record: RppPrizeCoupon) => (
                    <Tag variant={record.isActive ? 'success' : 'grey'}>
                        {record.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                ),
            },
            {
                title: 'Dates',
                key: 'dates',
                width: '22%',
                ellipsis: true,
                render: (_: unknown, record: RppPrizeCoupon) =>
                    `${convertUtcToLocalString(
                        record.startDate,
                        record.timezone,
                        LIST_DATE_FORMAT
                    )} → ${convertUtcToLocalString(
                        record.endDate,
                        record.timezone,
                        LIST_DATE_FORMAT
                    )}`,
            },
        ],
        []
    );

    return (
        <>
            <PageTopBar title="RPP Prize Coupons">
                <CreateButton
                    onClick={() => navigation.goToRppPrizeCouponCreate()}
                />
            </PageTopBar>

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div style={{ width: 220 }}>
                        <SearchInput
                            searchValue={searchValue}
                            onClickIcon={handleSearchClick}
                            onChange={handleOnChange}
                            onKeyDown={handleOnKeyDown}
                            placeholder="Id or title"
                        />
                    </div>
                    <Select<RppPrizeCouponType | 'all'>
                        value={search.params.type ?? 'all'}
                        style={{ width: 160 }}
                        onChange={(value) =>
                            search.setParams({
                                type: value === 'all' ? undefined : value,
                                page: 0,
                            })
                        }
                    >
                        <Select.Option value="all">All types</Select.Option>
                        <Select.Option value="coupon">Coupons</Select.Option>
                        <Select.Option value="in_store">
                            In-store promos
                        </Select.Option>
                    </Select>
                </div>

                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: store.coupons?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            {store.isFetchingList && !store.coupons ? (
                <Spin />
            ) : (
                <Table<RppPrizeCoupon>
                    className={`${style.table} clickable_table_body_rows`}
                    tableLayout="fixed"
                    rowKey="id"
                    dataSource={store.coupons?.results}
                    columns={columns}
                    loading={store.isFetchingList}
                    pagination={false}
                    bordered
                    onRow={(record) => ({
                        onClick: () =>
                            navigation.goToRppPrizeCouponEdit(record.id),
                    })}
                />
            )}
        </>
    );
});
